/**
 * Station catalog and commute profile definitions.
 *
 * Nothing here is secret. Origin anchors are deliberately coarse public landmarks; the real
 * home coordinates live in `ORIGIN_HOME_*` (Cloudflare secret / .dev.vars) or in the browser's
 * local override, and only ever refine profile matching. The drive itself always routes from
 * live GPS, so the app never needs a home address to work.
 */

import type { Coordinates, ProfileMode, StationSummary } from "../shared/types";

export const BART_STATIONS: Record<string, StationSummary> = {
	CIVC: { id: "CIVC", name: "Civic Center", lat: 37.779732, lon: -122.414123 },
	POWL: { id: "POWL", name: "Powell St", lat: 37.784471, lon: -122.407974 },
	MONT: { id: "MONT", name: "Montgomery St", lat: 37.789405, lon: -122.401066 },
	EMBR: { id: "EMBR", name: "Embarcadero", lat: 37.792874, lon: -122.39702 },
	"16TH": { id: "16TH", name: "16th St Mission", lat: 37.765062, lon: -122.419694 },
	"24TH": { id: "24TH", name: "24th St Mission", lat: 37.75247, lon: -122.418143 },
	GLEN: { id: "GLEN", name: "Glen Park", lat: 37.733064, lon: -122.433817 },
	BALB: { id: "BALB", name: "Balboa Park", lat: 37.721585, lon: -122.447506 },
	DALY: { id: "DALY", name: "Daly City", lat: 37.706121, lon: -122.469081 },
	COLM: { id: "COLM", name: "Colma", lat: 37.684638, lon: -122.466233 },
	SSAN: { id: "SSAN", name: "South San Francisco", lat: 37.664174, lon: -122.444116 },
	SBRN: { id: "SBRN", name: "San Bruno", lat: 37.637761, lon: -122.416287 },
	SFIA: { id: "SFIA", name: "SFO", lat: 37.615966, lon: -122.392409 },
	MLBR: { id: "MLBR", name: "Millbrae", lat: 37.599787, lon: -122.386702 },
	WOAK: { id: "WOAK", name: "West Oakland", lat: 37.804872, lon: -122.29514 },
	"12TH": { id: "12TH", name: "12th St Oakland", lat: 37.803664, lon: -122.271604 },
	"19TH": { id: "19TH", name: "19th St Oakland", lat: 37.80835, lon: -122.268602 },
	MCAR: { id: "MCAR", name: "MacArthur", lat: 37.828415, lon: -122.267105 },
	ROCK: { id: "ROCK", name: "Rockridge", lat: 37.844364, lon: -122.251435 },
	ORIN: { id: "ORIN", name: "Orinda", lat: 37.878361, lon: -122.183791 },
	LAFY: { id: "LAFY", name: "Lafayette", lat: 37.893394, lon: -122.123801 },
	WCRK: { id: "WCRK", name: "Walnut Creek", lat: 37.905628, lon: -122.067423 },
	PHIL: { id: "PHIL", name: "Pleasant Hill", lat: 37.928403, lon: -122.056013 },
};

/** How the rider reaches the boarding platform. */
export interface AccessLeg {
	kind: "drive" | "fixed" | "none";
	label: string;
	/** Used when the routing provider is unavailable, or as the whole value for `fixed`. */
	fallbackSeconds: number;
}

export type OriginAnchor =
	| {
			/**
			 * The drive itself, not a circle around a point. You are on this commute from the
			 * moment you leave the house until you reach the platform, so matching follows the
			 * route. Waypoints run home first, boarding station last.
			 */
			kind: "corridor";
			/** Secret holding the precise home coordinate, which replaces the first waypoint. */
			secretKey: "ORIGIN_HOME_PRIMARY" | "ORIGIN_HOME_ALTERNATE";
			waypoints: Coordinates[];
			halfWidthMeters: number;
	  }
	| { kind: "station"; stationId: string; radiusMeters: number };

export interface TargetArrival {
	stationId: string;
	/** Preferred arrival time at the final destination, minutes after local midnight. */
	targetMinutes: number;
	/** Arriving this far early is still fine. */
	earlyToleranceMinutes: number;
	/** Arriving this far after the target is tolerable; beyond it the plan is late. */
	lateToleranceMinutes: number;
}

export interface ProfileDefinition {
	id: string;
	label: string;
	shortLabel: string;
	mode: ProfileMode;
	boardingStationId: string;
	/** Index 0 is the primary destination and drives ranking. */
	destinationStationIds: string[];
	/** One name for several stations, e.g. two SF stops that are both simply "home". */
	destinationLabel?: string;
	origin: OriginAnchor;
	access: AccessLeg;
	stationBufferSeconds: number;
	/** Bathroom, keys, walk to the car: time before the drive can start. */
	prepSeconds: number;
	/** Platform to the real destination at the far end. */
	egressSeconds: number;
	/** Where the rider ends up, when that is not the arrival station. */
	finalLabel: string | null;
	/** And where that is, so getting there is something the phone can notice. */
	finalLocation: Coordinates | null;
	/** How close counts as being there. Null falls back to the general arrival fence. */
	finalRadiusMeters: number | null;
	/** Absent for profiles ranked purely by "what's next", such as the ride home. */
	target?: TargetArrival;
	/**
	 * Minutes after local midnight during which this profile is the likely one. Used only to
	 * break ties when position is unavailable or ambiguous, never to override a clear GPS fix.
	 */
	preferredWindowMinutes: { startMinutes: number; endMinutes: number };
	/** How far ahead to look for candidate trains. */
	lookaheadMinutes: number;
	/** How far back to look, so a just-missed train can still be shown honestly. */
	lookbehindMinutes: number;
	/** Catchable trains to put on screen, counting from the recommended one. */
	visibleTrains: number;
	/**
	 * Departed trains to keep behind the deck, overriding the rider's phase.
	 *
	 * Set where standing on the platform is the whole premise, so the deck does not depend on
	 * whether the phone has worked out yet that he is there.
	 */
	departedTrains?: number;
}

/**
 * What a station means to this rider when it is the *origin* of a trip.
 *
 * Keeping this per station rather than per route is what lets the manual picker offer any
 * combination of the known stations without a matrix of hand-written profiles: pick an origin
 * and you have inherited how he gets there, how long the faregates take, and where "near here"
 * is for automatic detection.
 */
export interface StationRole {
	id: string;
	label: string;
	access: AccessLeg;
	stationBufferSeconds: number;
	/** Getting out of the house and into the car. Zero when the trip starts on a platform. */
	prepSeconds: number;
	origin: OriginAnchor;
	preferredWindowMinutes: { startMinutes: number; endMinutes: number };
}

const MORNING = { startMinutes: 4 * 60, endMinutes: 11 * 60 };
const AFTERNOON = { startMinutes: 11 * 60, endMinutes: 23 * 60 };

/** The one ride-home profile. Named because the worker has to re-plan his ride without a phone. */
export const HOME_PROFILE_ID = "home-pm";

export const STATION_ROLES: Record<string, StationRole> = {
	CIVC: {
		id: "CIVC",
		label: "Civic Center",
		access: { kind: "drive", label: "Drive", fallbackSeconds: 20 * 60 },
		stationBufferSeconds: 300,
		prepSeconds: 120,
		origin: {
			kind: "corridor",
			secretKey: "ORIGIN_HOME_PRIMARY",
			// Public landmarks along the Richmond-to-Civic-Center run: Ocean Beach, Balboa at
			// Park Presidio, the Panhandle, Market at Van Ness. No address is committed here.
			waypoints: [
				{ lat: 37.7765, lon: -122.5095 },
				{ lat: 37.7776, lon: -122.4735 },
				{ lat: 37.7735, lon: -122.453 },
				{ lat: 37.7752, lon: -122.422 },
				BART_STATIONS.CIVC,
			],
			halfWidthMeters: 1400,
		},
		preferredWindowMinutes: MORNING,
	},
	GLEN: {
		id: "GLEN",
		label: "Glen Park",
		access: { kind: "drive", label: "Drive", fallbackSeconds: 10 * 60 },
		stationBufferSeconds: 300,
		prepSeconds: 120,
		origin: {
			kind: "corridor",
			secretKey: "ORIGIN_HOME_ALTERNATE",
			// Twin Peaks summit down through Portola and Diamond Heights to Glen Park.
			waypoints: [
				{ lat: 37.7544, lon: -122.4477 },
				{ lat: 37.7455, lon: -122.441 },
				{ lat: 37.7395, lon: -122.4405 },
				BART_STATIONS.GLEN,
			],
			halfWidthMeters: 1100,
		},
		preferredWindowMinutes: MORNING,
	},
	ROCK: {
		id: "ROCK",
		label: "Rockridge",
		// He walks up from school, so there is no travel leg to predict — only the faregates.
		access: { kind: "none", label: "On platform", fallbackSeconds: 0 },
		stationBufferSeconds: 120,
		prepSeconds: 0,
		origin: { kind: "station", stationId: "ROCK", radiusMeters: 3000 },
		preferredWindowMinutes: AFTERNOON,
	},
};

/**
 * What a station means as the *end* of a trip.
 *
 * The deadline belongs to the place the rider is going, not to the platform they get off at,
 * so the walk at the far end is part of the plan rather than slack hidden in a buffer.
 */
export interface DestinationRole {
	stationId: string;
	/** Where they are actually going. Null when the station is the destination. */
	finalLabel: string | null;
	/** Its coordinates, for noticing the last step of the journey. */
	finalLocation: Coordinates | null;
	/** How close counts as being there. Null falls back to the general arrival fence. */
	finalRadiusMeters: number | null;
	/** Platform to that place. */
	egressSeconds: number;
	target?: TargetArrival;
}

/**
 * The school property is long and narrow, so distance from one point cannot say whether the
 * rider has left it. Simplified from the mapped campus boundary around the occupied grounds.
 */
export const COLLEGE_PREP_BOUNDARY: readonly Coordinates[] = [
	{ lat: 37.848877, lon: -122.241492 },
	{ lat: 37.849435, lon: -122.240001 },
	{ lat: 37.848947, lon: -122.23963 },
	{ lat: 37.848615, lon: -122.239134 },
	{ lat: 37.847829, lon: -122.238045 },
	{ lat: 37.847417, lon: -122.23843 },
	{ lat: 37.846966, lon: -122.23861 },
	{ lat: 37.84759, lon: -122.239351 },
	{ lat: 37.848015, lon: -122.24042 },
	{ lat: 37.848418, lon: -122.241115 },
	{ lat: 37.848598, lon: -122.241499 },
	{ lat: 37.8487, lon: -122.241727 },
];

export const DESTINATION_ROLES: Record<string, DestinationRole> = {
	ROCK: {
		stationId: "ROCK",
		finalLabel: "College Prep",
		// The middle of the car park he is dropped in and picked up from, about 1.1 km uphill from
		// the station, so its geofence can never be confused with Rockridge's.
		finalLocation: { lat: 37.848507, lon: -122.24026 },
		// Retained for clients without bounded-place support; current clients use the campus polygon.
		finalRadiusMeters: 76,
		// Shuttle plus the walk at either end, not just walking.
		egressSeconds: 7 * 60,
		// First bell is 8:20 and class is 8:25. 8:05 is the time worth aiming at, and the window is
		// tight early and loose late so the plan leans towards being there first.
		target: { stationId: "ROCK", targetMinutes: 8 * 60 + 5, earlyToleranceMinutes: 6, lateToleranceMinutes: 13 },
	},
	CIVC: { stationId: "CIVC", finalLabel: null, finalLocation: null, finalRadiusMeters: null, egressSeconds: 0 },
	GLEN: { stationId: "GLEN", finalLabel: null, finalLocation: null, finalRadiusMeters: null, egressSeconds: 0 },
};

/** The stations the manual picker offers, in menu order. */
export const SELECTABLE_STATION_IDS = ["CIVC", "GLEN", "ROCK"];

export const PROFILES: ProfileDefinition[] = [
	{
		id: "civic-am",
		label: "Civic Center → Rockridge",
		shortLabel: "Civic Center",
		mode: "outbound",
		boardingStationId: "CIVC",
		destinationStationIds: ["ROCK"],
		origin: STATION_ROLES.CIVC.origin,
		access: STATION_ROLES.CIVC.access,
		stationBufferSeconds: STATION_ROLES.CIVC.stationBufferSeconds,
		prepSeconds: STATION_ROLES.CIVC.prepSeconds,
		egressSeconds: DESTINATION_ROLES.ROCK.egressSeconds,
		finalLabel: DESTINATION_ROLES.ROCK.finalLabel,
		finalLocation: DESTINATION_ROLES.ROCK.finalLocation,
		finalRadiusMeters: DESTINATION_ROLES.ROCK.finalRadiusMeters,
		target: DESTINATION_ROLES.ROCK.target,
		preferredWindowMinutes: MORNING,
		lookaheadMinutes: 120,
		lookbehindMinutes: 20,
		visibleTrains: 5,
	},
	{
		id: "glen-am",
		label: "Glen Park → Rockridge",
		shortLabel: "Glen Park",
		mode: "outbound",
		boardingStationId: "GLEN",
		destinationStationIds: ["ROCK"],
		origin: STATION_ROLES.GLEN.origin,
		access: STATION_ROLES.GLEN.access,
		stationBufferSeconds: STATION_ROLES.GLEN.stationBufferSeconds,
		prepSeconds: STATION_ROLES.GLEN.prepSeconds,
		egressSeconds: DESTINATION_ROLES.ROCK.egressSeconds,
		finalLabel: DESTINATION_ROLES.ROCK.finalLabel,
		finalLocation: DESTINATION_ROLES.ROCK.finalLocation,
		finalRadiusMeters: DESTINATION_ROLES.ROCK.finalRadiusMeters,
		target: DESTINATION_ROLES.ROCK.target,
		preferredWindowMinutes: MORNING,
		lookaheadMinutes: 120,
		lookbehindMinutes: 20,
		visibleTrains: 5,
	},
	{
		// The ride home is not a departure-time decision: he is already at the station, so there
		// is no travel leg and no target window. It is "which of the next trains, and when do
		// they reach each of the two homes".
		id: HOME_PROFILE_ID,
		label: "Rockridge → San Francisco",
		shortLabel: "Going home",
		mode: "return",
		boardingStationId: "ROCK",
		// SFIA (SFO) is a manually-scheduled third destination, not a third house — Eben flies out of
		// it occasionally, and it shares the ride-home's flat no-final-leg shape, egress and all.
		destinationStationIds: ["CIVC", "GLEN", "SFIA"],
		destinationLabel: "San Francisco",
		origin: STATION_ROLES.ROCK.origin,
		access: STATION_ROLES.ROCK.access,
		stationBufferSeconds: STATION_ROLES.ROCK.stationBufferSeconds,
		prepSeconds: STATION_ROLES.ROCK.prepSeconds,
		egressSeconds: 0,
		finalLabel: null,
		finalLocation: null,
		finalRadiusMeters: null,
		preferredWindowMinutes: AFTERNOON,
		lookaheadMinutes: 90,
		// Wide enough that the platform deck has trains behind it, which is what the ride home
		// is: standing there, watching one go, and asking what the next one costs.
		lookbehindMinutes: 20,
		visibleTrains: 5,
		departedTrains: 1,
	},
];

/** The morning that leaves from this station, which is the other face of the same choice. */
export function morningProfileForStation(stationId: string | null): ProfileDefinition | null {
	if (!stationId) {
		return null;
	}
	return PROFILES.find((profile) => profile.mode === "outbound" && profile.boardingStationId === stationId) ?? null;
}

export function profileById(id: string | undefined | null): ProfileDefinition | null {
	if (!id) {
		return null;
	}
	return PROFILES.find((profile) => profile.id === id) ?? null;
}

/**
 * Whether the rider has to get to the platform, rather than already standing on it.
 *
 * This is the question the card, the metrics and the leave-by time actually turn on. Reading
 * it off the mode instead was the same answer only for as long as there were two modes.
 */
export function hasAccessLeg(profile: ProfileDefinition): boolean {
	return profile.access.kind !== "none";
}

/**
 * A profile for an explicitly chosen origin and destination.
 *
 * Returns an existing profile when the pair already has one, so picking Rockridge → Civic
 * Center by hand still gets the two-destination ride home rather than a narrower copy of it.
 */
export function routeProfile(fromId: string, toId: string): ProfileDefinition | null {
	if (fromId === toId) {
		return null;
	}
	if (!SELECTABLE_STATION_IDS.includes(fromId) || !SELECTABLE_STATION_IDS.includes(toId)) {
		return null;
	}

	const existing = PROFILES.find(
		(profile) => profile.boardingStationId === fromId && profile.destinationStationIds[0] === toId,
	);
	if (existing) {
		return existing;
	}

	const role = STATION_ROLES[fromId];
	if (!role) {
		return null;
	}
	const destination = DESTINATION_ROLES[toId];

	return {
		id: `route:${fromId}-${toId}`,
		label: `${BART_STATIONS[fromId].name} → ${BART_STATIONS[toId].name}`,
		shortLabel: BART_STATIONS[toId].name,
		mode: role.access.kind === "drive" ? "outbound" : "return",
		boardingStationId: fromId,
		destinationStationIds: [toId],
		origin: role.origin,
		access: role.access,
		stationBufferSeconds: role.stationBufferSeconds,
		prepSeconds: role.prepSeconds,
		egressSeconds: destination?.egressSeconds ?? 0,
		finalLabel: destination?.finalLabel ?? null,
		finalLocation: destination?.finalLocation ?? null,
		finalRadiusMeters: destination?.finalRadiusMeters ?? null,
		target: destination?.target,
		preferredWindowMinutes: role.preferredWindowMinutes,
		lookaheadMinutes: 120,
		lookbehindMinutes: 20,
		visibleTrains: 5,
	};
}

export const DEFAULT_PROFILE_ID = "civic-am";
